import React, { useMemo, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { X } from "lucide-react";
import { useAppDispatch } from "../../../hooks";
import { updateStore } from "../../../slices/storeSlice";
import type { Store } from "../../../types/store";
import type { EmployeeItem } from "../../../types/employee";
import { toast } from "react-toastify";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    store: Store;
    employees: EmployeeItem[];
    onComplete: () => void;
}


const UpdateStoreManagerInfoModal: React.FC<Props> = ({
    isOpen,
    onClose,
    store,
    employees,
    onComplete
}) => {
    const dispatch = useAppDispatch();
    const [search, setSearch] = useState("");


    const validationSchema = Yup.object({
        managerIds: Yup.array()
            .of(Yup.string())
            .test(
                "at-least-one-if-available",
                "At least one manager is required",
                function (value) {
                    // If there are no employees to choose from, allow empty
                    if (!employees || employees.length === 0) return true;

                    // Otherwise require at least one
                    return Array.isArray(value) && value.length > 0;
                }
            ),
    });


    /** Initial selected managers */
    const initialValues = {
        managerIds: Array.isArray(store.managers)
            ? store.managers.map((m) => m.id)
            : [],
    };


    // console.log("Mangers: ", employees);

    /** Search filter */
    const filteredEmployees = useMemo(() => {
        if (!search.trim()) return [];
        return employees.filter((e) => {
            const fullName = `${e.firstname} ${e.othername ?? ""} ${e.lastname}`.toLowerCase();
            return fullName.includes(search.toLowerCase());
        });
    }, [search, employees]);


    const handleSubmit = async (values: any, { setSubmitting }: any) => {
        try {
            await dispatch(
                updateStore({
                    id: store.id,
                    data: { managerIds: values.managerIds },
                })
            ).unwrap();
            onComplete();
            onClose();
        } catch (error) {
            console.error("Failed to update store: ", error);
            toast.error("Failed to update store. Please try again");
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[999]">
            <div className="bg-white w-full max-w-xl p-6 rounded-2xl shadow-lg max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Update Store Managers
                </h3>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue, errors, touched, isSubmitting }) => (
                        <Form className="space-y-4">
                            {/* --- SEARCH --- */}
                            <div>
                                <label className="form-label">Search Managers</label>

                                {employees.length === 0 ? (
                                    <p className="text-sm text-gray-400 mt-1">
                                        No employees available yet. Please add employees before assigning managers.
                                    </p>
                                ) : (
                                    <input
                                        type="text"
                                        className="form-input mt-1 w-full"
                                        placeholder="Search by name"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                )}
                            </div>


                            {/* --- SEARCH RESULTS --- */}
                            {search && employees.length > 0 && (
                                <div className="border rounded-lg p-2 max-h-48 overflow-y-auto">
                                    {filteredEmployees.length === 0 && (
                                        <p className="text-sm text-gray-400">No results</p>
                                    )}

                                    {filteredEmployees.map((emp) => {
                                        const isSelected = values.managerIds.includes(emp.id);
                                        const fullName = [emp.firstname, emp.lastname].filter(Boolean).join(" ");

                                        return (
                                            <button
                                                type="button"
                                                key={emp.id}
                                                disabled={isSelected}
                                                onClick={() =>
                                                    setFieldValue("managerIds", [...values.managerIds, emp.id])
                                                }
                                                className={`w-full text-left px-3 py-2 rounded-md text-sm transition ${isSelected
                                                    ? "bg-gray-200 cursor-not-allowed"
                                                    : "hover:bg-gray-100"
                                                    }`}
                                            >
                                                {fullName}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {/* --- SELECTED MANAGERS --- */}
                            <div>
                                <p className="form-label mb-1">Selected Managers</p>
                                <div className="flex flex-wrap gap-2">
                                    {values.managerIds.length === 0 && (
                                        <p className="text-sm text-gray-400">
                                            No managers selected yet.
                                            {employees.length === 0 && " Add employees first to assign managers."}
                                        </p>
                                    )}


                                    {values.managerIds.map((id: string) => {
                                        const emp = employees.find((e) => e.id === id);
                                        if (!emp) return null;

                                        const fullName = [
                                            emp.firstname,
                                            emp.othername,
                                            emp.lastname,
                                        ]
                                            .filter(Boolean)
                                            .join(" ");

                                        return (
                                            <div
                                                key={id}
                                                className="flex items-center gap-2 px-3 py-1 rounded-full bg-primary text-white text-sm"
                                            >
                                                {fullName}
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setFieldValue(
                                                            "managerIds",
                                                            values.managerIds.filter((mid: string) => mid !== id)
                                                        )
                                                    }
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>

                                {errors.managerIds && touched.managerIds && (
                                    <p className="text-xs text-red-500 mt-1">
                                        {errors.managerIds as string}
                                    </p>
                                )}
                            </div>


                            {/* --- ACTIONS --- */}
                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    className="modal-btn-secondary"
                                    onClick={onClose}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="modal-btn-primary"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="loader"></span>
                                            <span>Saving</span>
                                        </>
                                    ) :
                                        "Save Changes"
                                    }
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default UpdateStoreManagerInfoModal;
